import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import CancelButton from "../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../components/ConfirmBoxModal";
import SubmitHookForm from "../../../../components/SubmitHookForm";
import { toast } from "../../../../components/toastNotification";
import {
  leShowState,
  ledAddState,
  ledEditState,
  rowForEditLE,
  leasedEquipmentSaving,
  setLinkStateLeasedEquipment,
  rowForEditLeasedEquipment,
  leAttachments,
  leAttachmentsForDelete,
  leFromOffice,
  disableLEOfficeField,
  leFromVendor,
  vendorLeasedId,
  leasedEquipmentForEdit,
  leEditMassUpload,
  leMassUploadData,
  leAllMassUploadData,
} from "../atom/atom";
import { LeasedEquipmentDetails } from "./LeasedEquipmentDetails";
import { ILeasedEquipmentDetails } from "../type";
import useAddLeasedEquipment from "../hooks/useAddLeasedEquipment";
import useEditLeasedEquipment from "../hooks/useEditLeasedEquipment";
import { useGetLeasedEquipmentForEdit } from "../hooks/useGetLeasedEquipmentForEdit";
import { useGetLeasedEquipmentByRecordNumber } from "../hooks/useGetLeasedEquipmentByRecordNumber";
import { CarrierAgreementMgmtTabs } from "../../carrierAgreementTabs";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { mainSelectedTab } from "../../atoms";
import { VendorTabs } from "../../../VendorTab/VendorTab";
import { OfficeTabs } from "../../../OfficeTab/OfficeTabs";
import { vendorViewId } from "../../Vendor/atoms/atoms";
import useValidateData from "../hooks/useValidateData";
import { updateSafeMake } from "actions/Utilities/SafeMake";

interface LeasedEquipmentFormProp {
  isAdd: boolean;
  isEdit: boolean;
  setIsAdd;
  setIsEdit;
  isModalEdit: boolean;
  isOffice: boolean;

  closeModal?: () => void;
}

export const LeasedEquipmentForm: React.FC<LeasedEquipmentFormProp> = ({
  setIsAdd,
  setIsEdit,
  isAdd,
  isEdit,
  isModalEdit,
  isOffice,
  closeModal,
}) => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const [isLinkedState] = useRecoilState(setLinkStateLeasedEquipment);
  const [, setSelectedIndex] = useRecoilState(mainSelectedTab);
  const [isShow, setShowState] = useRecoilState(leShowState);
  const [isAddState, setAddState] = useRecoilState(ledAddState);
  const attachments = useRecoilValue<(File | null)[]>(leAttachments);
  const [attachmentsForDelete, setAttachmentsForDelete] = useRecoilState(
    leAttachmentsForDelete
  );
  const [massUploadData, setMassUploadData] =
    useRecoilState<any>(leMassUploadData);
  const [rowForEditLE, setRowForEditLE] = useRecoilState(
    rowForEditLeasedEquipment
  );

  const [isEditMassUpload, setEditMassUpload] =
    useRecoilState(leEditMassUpload);
  const [saveModal, setSaveModal] = useState(false);
  const [isSaving, setIsSaving] = useRecoilState(leasedEquipmentSaving);
  const [showFromOffice, setShowFromOffice] = useRecoilState(leFromOffice);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [leasedEquipmentDetails, setLeasedEquipmentDetails] = useState<any>();
  const [disableOfficeField, setDisableOfficeField] =
    useRecoilState(disableLEOfficeField);
  const [showFromLEVendor, setShowFromVendor] = useRecoilState(leFromVendor);
  const leVendorId = useRecoilValue(vendorLeasedId);
  const vendorId = useRecoilValue(vendorViewId);
  const [forRevalMassUpload, setForRevalMassUpload] = useState([]);
  const { mutateAsync: validateLE, isLoading, error } = useValidateData();
  const [newData, setNewData] = useState<ILeasedEquipmentDetails>();
  const [isEditState, setEditState] = useRecoilState(ledEditState);
  const [rowForEdit, setRowToEdit] = useRecoilState<any>(
    leasedEquipmentForEdit
  );

  const [allMassUploadData, setAllMassUpload] =
    useRecoilState(leAllMassUploadData);
  const useFormMethods = useForm<ILeasedEquipmentDetails>({
    defaultValues: { ...rowForEditLE },
  });

  const {
    formState: { isDirty },
  } = useFormMethods;

  const handleLink = () => {
    history.push({
      pathname: "/carrieragreementmgmt/leasedequipments",
    });
    window.location.reload();
  };

  //Add Leased Equipment
  const {
    mutate: addLeasedEquipment,
    isSuccess: addSuccess,
    isError: addError,
    data: addResult,
    reset: resetAddData,
  } = useAddLeasedEquipment();

  //Edit Leased Equipment
  const {
    mutate: editLeasedEquipment,
    isSuccess: editSuccess,
    isError: editError,
    data: editResult,
    reset: resetEditData,
  } = useEditLeasedEquipment();

  const {
    data: leasedEquipmentData,
    isFetching,
    isSuccess,
  } = useGetLeasedEquipmentByRecordNumber(rowForEditLE?.recordNumber);

  useEffect(() => {
    if (isSuccess && leasedEquipmentData) {
      setLeasedEquipmentDetails(leasedEquipmentData);
    }
  }, [isSuccess, leasedEquipmentData]);

  const onSubmit = (data: ILeasedEquipmentDetails, e) => {
    setNewData({
      ...data,
      id: data.id,
      isActive: data.isActive,
      createdDate: data.createdDate
        ? new Date(data.createdDate).toISOString()
        : null,
      equipmentReturned: data.equipmentReturned
        ? data.equipmentReturned
        : false,
      baseUrl: window.location.origin,
      attachments: attachments,
      attachmentsForDelete: attachmentsForDelete,
    });

    if (isAddState) {
      setSaveModal(true);
    } else if (isEditMassUpload && isDirty) {
      setSaveModal(true);
      setForRevalMassUpload(
        massUploadData.map((item) => {
          if (item.id === data.id) {
            return {
              ...data,
              type: data.typeId === null ? "" : data.type,
              model: data.modelId === null ? "" : data.model,
              make: data.makeId === null ? "" : data.make,
              supplyAlertProfile:
                data.supplyAlertProfileId === null
                  ? ""
                  : data.supplyAlertProfile,

              status: data.statusId === null ? "" : data.status,
              servicingDealer:
                data.vendorId === null ? "" : data.servicingDealer,
              condition: "revalidate",
            };
          } else {
            return item;
          }
        })
      );
    } else if (
      (isEdit && isDirty) ||
      attachmentsForDelete?.length > 0 ||
      attachments?.length > 0
    ) {
      setSaveModal(true);
      setIsSaving(false);
    } else {
      setIsSaving(false);

      if (!isDirty) {
        toast.fire("Saving Failed", "No Changes have been made", "warning");
      }
    }
  };
  // redirection back to office
  const handleLinkOffice = (moduleName) => {
    const url = `/carrieragreementmgmt/${moduleName}`;
    history.push(moduleName);
    let userTabs = [
      {
        to: "",
      },
    ];
    [...CarrierAgreementMgmtTabs].forEach((tabs) => {
      let temp = [...userPermissions].filter(
        (user) => user === tabs.permission
      );
      if (temp.length > 0)
        userTabs.push({
          to: tabs.to,
        });
    });

    userTabs.shift();
    const index = userTabs.findIndex((c) => c.to === url);
    setSelectedIndex(index);
  };

  // redirection back to vendor
  const handleLinkVendor = () => {
    const url = `/officesettings/leasedequipment`;
    history.push({
      pathname: `/vendortab/vendor`,
      state: {
        action: "leCancel",
        vendorId: leVendorId ? leVendorId : vendorId,
      },
    });
    window.location.reload();
    let userTabs = [
      {
        to: "",
      },
    ];
    [...OfficeTabs].forEach((tabs) => {
      let temp = [...userPermissions].filter(
        (user) => user === tabs.permission
      );
      if (temp.length > 0)
        userTabs.push({
          to: tabs.to,
        });
    });

    userTabs.shift();
    const index = userTabs.findIndex((c) => c.to === url);
    setSelectedIndex(index);
  };

  const handleSave = () => {
    setSaveModal(false);
    if (isAddState) {
      
      addLeasedEquipment({ ...newData });

      if (isLinkedState) {
        setTimeout(() => handleLink(), 5000);
      }
    } else if (isEditMassUpload) {
      const updatedLeasedEquipment = useFormMethods.getValues();

      setRowForEditLE(updatedLeasedEquipment);

      setIsSaving(false);

      validateMasUpload();

      setShowFromOffice(false);

      if (closeModal) {
        closeModal();
      }
    } else {
      editLeasedEquipment(newData);
      if (isLinkedState) {
        setTimeout(() => handleLink(), 5000);
      }
    }
  };

  const validateMasUpload = async () => {
    const response = await validateLE({
      updatedLe: forRevalMassUpload,
      isOffice: isOffice,
    });

    if (response.status === 200) {
      setRowToEdit([]);
      setEditMassUpload(false);
      setMassUploadData(response.data);
      setAllMassUpload(response.data);
      toast.fire(
        "Success",
        "Leased Equipment Updated Successfully.",
        "success"
      );
    } else {
      toast.fire(
        "Updating Leased Equipment failed",
        "Server Error: Cannot Update Leased Equipment",
        "error"
      );
    }
  };

  const handleCancel = () => {
    if (closeModal) {
      closeModal();
    }
    if (showFromOffice) {
      setShowFromOffice(false);
      handleLinkOffice("officesetting");
    }

    if (showFromLEVendor) {
      setShowFromVendor(false);
      handleLinkVendor();
    }

    useFormMethods.reset();
    useFormMethods.clearErrors();
    setDisableOfficeField(false);
    setAddState(false);
    setEditState(false);
    setIsAdd(false);
    setIsSaving(false);
    setIsEdit(false);
    setRowForEditLE(undefined);
    setShowFromOffice(false);
    setShowFromVendor(false);
  };

  //Add Result
  if (addSuccess && addResult) {
    if (addResult.status === 501) {
      toast.fire("Failed", "Leased Equipment Record Already Exists.", "error");
      setIsSaving(!isSaving);
      setSaveModal(false);
    } else if (addResult.status === 201 || addResult.status === 200) {
      toast.fire("Success", "Leased Equipment Added Successfully.", "success");
      queryClient.invalidateQueries("GetLeasedEquipment");
      handleCancel();
      setAddState(false);
      setIsAdd(false);
    } else if (addResult.status === 500) {
      if (
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "New Leased Equipment added successfully, but " +
            addResult.data.message,
          "warning"
        );
        handleCancel();
      } else {
        toast.fire(
          "Failed",
          "Failed to Add Leased Equipment! No Data Saved",
          "error"
        );
      }
      queryClient.invalidateQueries("GetLeasedEquipment");
      setAddState(false);
      setIsAdd(false);
    } else {
      toast.fire(
        "Server Error",
        "Failed to Add Leased Equipment! No Data Saved",
        "error"
      );
      queryClient.invalidateQueries("GetLeasedEquipment");
      setAddState(false);
    }
    resetAddData();
    setSaveModal(false);
    setIsSaving(false);
  }
  if (addError) {
    setIsSaving(false);
    setSaveModal(false);
    toast.fire(
      "Server Error",
      "Failed to Add Leased Equipment! No Data Saved",
      "error"
    );
    queryClient.invalidateQueries("GetLeasedEquipment");
    setAddState(false);
  }

  //Edit Result
  if (editSuccess && editResult) {
    if (editResult.status === 501) {
      toast.fire("Failed", "Leased Equipment Record Already Exists.", "error");
      setIsSaving(!isSaving);
      setSaveModal(false);
    } else if (editResult.status === 201 || editResult.status === 200) {
      toast.fire(
        "Success",
        "Leased Equipment Updated Successfully.",
        "success"
      );
      queryClient.invalidateQueries("GetLeasedEquipment");
      handleCancel();
      setEditState(false);
      setEditState(false);
    } else if (editResult.status === 500) {
      if (
        editResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        editResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "New Leased Equipment updated successfully, but " +
            editResult.data.message,
          "warning"
        );
        handleCancel();
      } else {
        toast.fire(
          "Failed",
          "Failed to Update Leased Equipment! No Data Saved",
          "error"
        );
      }
      queryClient.invalidateQueries("GetLeasedEquipment");
      setEditState(false);
      setIsEdit(false);
    } else {
      toast.fire(
        "Server Error",
        "Failed to Update Leased Equipment! No Data Saved",
        "error"
      );
      queryClient.invalidateQueries("GetLeasedEquipment");
      setEditState(false);
    }
    resetEditData();
  }
  if (editError) {
    setIsSaving(false);
    setSaveModal(false);
    toast.fire(
      "Server Error",
      "Failed to Update Leased Equipment! No Data Saved",
      "error"
    );
    queryClient.invalidateQueries("GetLeasedEquipment");
    setEditState(false);
  }

  return (
    <>
      <div className="p-3 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-2">
              <div className="ml-2 font-bold text-primary text-md">
                {isAddState ? "Add Leased Equipment" : "Edit Leased Equipment"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="submit"
                  disabled={isSaving}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={undefined}
                />
                <CancelButton
                  onClick={() => {
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="border-b-2 border-secondary mr-2 ml-2"></div>
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div>
            <LeasedEquipmentDetails
              rowForEdit={rowForEditLE}
              isModalEdit={isModalEdit}
              isOffice={isOffice}
            />
          </div>
        </FormProvider>
      </div>
      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={
            isAddState ? "Add Leased Equipment" : "Edit Leased Equipment"
          }
          qConfirmation={`Are you sure you want to ${
            isAddState ? "Add " : "Update "
          } this data?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            handleSave();
            // setIsSaving(true);
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};
